import { useMemo } from 'react'
import { getOffsetMapPairs } from '@utils/candidateFiltering'

interface OffsetMapStable {
  all_pairs: Record<string, any>
  metadata?: any
  extended_pairs?: Record<string, any>
  extended_connectivity?: Record<string | number, number[]>
}

interface UseConnectedFramesParams {
  manuallySelectedFrameIndex: number | null
  connectivityGraph: Map<number, Set<number>> | null
  extendedConnectivityGraph: Map<number, Set<number>> | null
  offsetMapStable: OffsetMapStable | null
  settings: {
    max3DDistance: number
    maxDz: number
  }
  reconstructionMethod?: 'colmap' | 'phase_correlation'
}

/**
 * Calculate context frame indices (extended connectivity frames not directly connected)
 */
export function useContextFrameIndices(
  manuallySelectedFrameIndex: number | null,
  extendedConnectivityGraph: Map<number, Set<number>> | null,
  connectivityGraph: Map<number, Set<number>> | null
): Set<number> {
  return useMemo(() => {
    const indices = new Set<number>()
    if (!manuallySelectedFrameIndex || !extendedConnectivityGraph || !connectivityGraph) {
      return indices
    }

    // Get directly connected frames
    const directlyConnected = connectivityGraph.get(manuallySelectedFrameIndex) || new Set()
    
    // Get extended reachable frames
    const extendedReachable = extendedConnectivityGraph.get(manuallySelectedFrameIndex) || new Set()
    
    // Context frames are those in extended graph but NOT directly connected
    for (const frameIdx of extendedReachable) {
      if (!directlyConnected.has(frameIdx) && frameIdx !== manuallySelectedFrameIndex) {
        indices.add(frameIdx)
      }
    }
    
    return indices
  }, [manuallySelectedFrameIndex, extendedConnectivityGraph, connectivityGraph])
}

/**
 * Calculate connected frames for selected frame, filtered by max 3D distance and max dZ
 */
export function useConnectedFrameIndices({
  manuallySelectedFrameIndex,
  connectivityGraph,
  offsetMapStable,
  settings,
  reconstructionMethod
}: UseConnectedFramesParams): Set<number> {
  return useMemo(() => {
    const filtered = new Set<number>()
    if (manuallySelectedFrameIndex === null || !connectivityGraph || !offsetMapStable) {
      return filtered
    }

    // For phase_correlation mode, use extended_connectivity instead of just direct connections
    let connectedSet: Set<number> | undefined
    
    if (reconstructionMethod === 'phase_correlation' && offsetMapStable.extended_connectivity) {
      const extConnectivity = offsetMapStable.extended_connectivity
      const extConnArray = extConnectivity[manuallySelectedFrameIndex] || extConnectivity[String(manuallySelectedFrameIndex)] || []
      connectedSet = new Set(extConnArray)
    } else {
      // Fallback to standard connectivity for COLMAP or if extended not available
      connectedSet = connectivityGraph.get(manuallySelectedFrameIndex)
    }
    
    if (!connectedSet) {
      return filtered
    }

    // Filter by max 3D distance and max dZ using offsetMap values
    connectedSet.forEach(idx => {
      // Try both directions: manuallySelectedFrameIndex_idx and idx_manuallySelectedFrameIndex
      let key = `${manuallySelectedFrameIndex}_${idx}`
      let offset = offsetMapStable.all_pairs[key]
      
      if (!offset) {
        // Try reverse direction
        key = `${idx}_${manuallySelectedFrameIndex}`
        offset = offsetMapStable.all_pairs[key]
      }
      
      if (!offset) {
        // For extended pairs, check extended_pairs if available
        if (reconstructionMethod === 'phase_correlation' && offsetMapStable.extended_pairs) {
          key = `${manuallySelectedFrameIndex}_${idx}`
          offset = offsetMapStable.extended_pairs[key]
          
          if (!offset) {
            key = `${idx}_${manuallySelectedFrameIndex}`
            offset = offsetMapStable.extended_pairs[key]
          }
        }
        
        if (!offset) {
          // No offset data found in either direction, include by default
          filtered.add(idx)
          return
        }
      }

      // Use pre-calculated 3D distance from offsetMap ONLY (dZ is NOT part of 3D distance!)
      if (offset.distance_3d === undefined) {
        filtered.add(idx)
        return
      }
      const distance3d = offset.distance_3d
      
      // dZ is SEPARATE from 3D distance (not part of the calculation)
      const dz = Math.abs(offset.dz ?? 0)

      // Check if within filter bounds
      const withinDistance = distance3d <= settings.max3DDistance
      const withinDz = dz <= settings.maxDz
      
      const passed = withinDistance && withinDz

      if (passed) {
        filtered.add(idx)
      }
    })

    return filtered
  }, [manuallySelectedFrameIndex, connectivityGraph, offsetMapStable, settings.max3DDistance, settings.maxDz, reconstructionMethod])
}

/**
 * Stabilize offsetMap to prevent unnecessary recalculations
 */
export function useStableOffsetMap(offsetMap: any): OffsetMapStable | null {
  return useMemo(() => {
    if (!offsetMap) return null
    return {
      all_pairs: getOffsetMapPairs(offsetMap),
      metadata: offsetMap.metadata,
      extended_pairs: offsetMap.extended_pairs,
      extended_connectivity: offsetMap.extended_connectivity
    }
  }, [offsetMap?.metadata?.generated])
}
